
import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { CheckCircle, AlertCircle } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";

const AuthVerify = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, loading } = useAuth();
  const [verificationState, setVerificationState] = useState<'loading' | 'success' | 'error'>('loading');
  const [errorMessage, setErrorMessage] = useState<string>("");
  const [isVerified, setIsVerified] = useState(false);

  useEffect(() => {
    const verifyUser = async () => {
      try {
        // Debug: Log the current URL and search params
        console.log('Current URL:', window.location.href);
        console.log('Search params:', Object.fromEntries(searchParams.entries()));
        
        const token = searchParams.get('token');
        const type = searchParams.get('type');

        console.log('Extracted params:', { token, type });

        if (!token || !type) {
          setErrorMessage("Ungültiger Bestätigungslink");
          setVerificationState('error');
          return;
        }

        console.log('Verifying user with token:', token, 'type:', type);

        const { data, error } = await supabase.auth.verifyOtp({
          token_hash: token,
          type: type as any,
        });

        if (error) {
          console.error('Verification error:', error);
          setErrorMessage("Bestätigung fehlgeschlagen: " + error.message);
          setVerificationState('error');
          return;
        }

        console.log('Verification successful:', data);
        // If this is a password recovery flow, go straight to reset-password
        if (type === 'recovery') {
          setVerificationState('success');
          toast.success("Verifizierung erfolgreich – bitte neues Passwort setzen");
          navigate('/reset-password', { replace: true });
          return;
        }
        setVerificationState('success');
        setIsVerified(true);
        toast.success("E-Mail erfolgreich bestätigt!");

      } catch (error) {
        console.error('Verification error:', error);
        setErrorMessage("Ein Fehler ist aufgetreten bei der Bestätigung");
        setVerificationState('error');
      }
    };

    verifyUser();
  }, [searchParams]);

  // Handle navigation after verification and auth state is ready
  useEffect(() => {
    if (isVerified && verificationState === 'success' && !loading && user) {
      console.log('Auth state is ready, processing referral from user metadata');
      
      // Check if there's a referral code in user metadata
      const referralCode = user.user_metadata?.referralCode;
      console.log('User metadata referral code:', referralCode);
      
      const timer = setTimeout(() => {
        if (referralCode) {
          // Redirect to invite page to process referral session
          navigate(`/invite?token=${referralCode}`, { replace: true });
        } else {
          // No referral, go to dashboard
          navigate('/dashboard', { replace: true });
        }
      }, 1000);

      return () => clearTimeout(timer);
    }
  }, [isVerified, verificationState, loading, user, navigate]);

  if (verificationState === 'loading') {
    return (
      <div className="min-h-screen bg-uplivion-lightBlue/30 flex items-center justify-center">
        <div className="text-center bg-white rounded-lg shadow-md p-8 max-w-md w-full mx-4">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-uplivion-blue mx-auto mb-4"></div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">E-Mail wird bestätigt...</h1>
          <p className="text-gray-600">Bitte warten Sie einen Moment.</p>
        </div>
      </div>
    );
  }

  if (verificationState === 'success') {
    return (
      <div className="min-h-screen bg-uplivion-lightBlue/30 flex items-center justify-center">
        <div className="text-center bg-white rounded-lg shadow-md p-8 max-w-md w-full mx-4">
          <div className="mx-auto w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-4">
            <CheckCircle className="w-8 h-8 text-green-600" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Willkommen bei uplivion!</h1>
          <p className="text-lg text-gray-600 mb-4">Deine E-Mail wurde erfolgreich bestätigt.</p>
          {loading && (
            <div className="flex items-center justify-center mb-4">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-uplivion-blue mr-2"></div>
              <p className="text-sm text-gray-500">Anmeldung wird abgeschlossen...</p>
            </div>
          )}
          {!loading && user && (
            <p className="text-sm text-gray-500">Du wirst automatisch zum Dashboard weitergeleitet...</p>
          )}
          {!loading && !user && (
            <div>
              <p className="text-sm text-gray-500 mb-4">Anmeldung wird verarbeitet...</p>
              <button
                onClick={() => navigate('/login')}
                className="bg-uplivion-blue text-white px-6 py-2 rounded-lg hover:bg-uplivion-blue/90 transition-colors"
              >
                Zur Anmeldung
              </button>
            </div>
          )}
        </div>
      </div>
    );
  }

  if (verificationState === 'error') {
    return (
      <div className="min-h-screen bg-uplivion-lightBlue/30 flex items-center justify-center">
        <div className="text-center bg-white rounded-lg shadow-md p-8 max-w-md w-full mx-4">
          <div className="mx-auto w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mb-4">
            <AlertCircle className="w-8 h-8 text-red-600" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Bestätigung fehlgeschlagen</h1>
          <p className="text-gray-600 mb-4">{errorMessage}</p>
          <button
            onClick={() => navigate('/login')}
            className="bg-uplivion-blue text-white px-6 py-2 rounded-lg hover:bg-uplivion-blue/90 transition-colors"
          >
            Zur Anmeldung
          </button>
        </div>
      </div>
    );
  }

  return null;
};

export default AuthVerify;
